import { Color } from '@coast/schemas';
import { isNil, isNumber } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { IconName } from '@shared/components/Icon';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';

/**
 * A `FilterByComponent` that may also carry `containsCurrentWorkflowTemplateComponentValue`, the
 * one branch the dynamic filter union has and the collection view's doesn't (see
 * `FilterComponentMatchSelection`).
 *
 * Declared as an extension rather than a union with `DynamicFilterByComponentFragment` so that a
 * value of this type is still a `FilterByComponentFragment` and every existing consumer - card
 * queries, saved views, bulk update - keeps working untouched. Only the related card field config
 * ever produces the extra branch; everywhere else it is simply absent.
 */
export type ApiFilterByComponent = Omit<API.FilterByComponentFragment, 'value'> & {
  value: API.FilterByComponentFragment['value'] & {
    containsCurrentWorkflowTemplateComponentValue?: { componentId: string } | null;
  };
};

/** `CollectionCriteria` over `ApiFilterByComponent`. Assignable to the generated fragment. */
export type ApiCollectionCriteria = Omit<
  API.CollectionCriteriaFragment,
  'filterByComponents' | 'groups'
> & {
  filterByComponents: ApiFilterByComponent[];
  groups: { filterByComponents: ApiFilterByComponent[] }[];
};

export type ControlOption = {
  uuid?: string;
  id: string;
  label: string;
  type?: string;
  icon?: IconName;
  component?: InputComponent;
};

/**
 * One `RELATED_CARD` hop on the way to a *nested* filter - the cross-workflow
 * (`matchesRelatedEntity`) case.
 */
export type RelatedEntityHop = {
  /** The `RELATED_CARD` component, on the workflow this hop starts from, that the filter goes through. */
  componentId: string;
  /** That related-card component's label, for the "Asset -> Meter -> Status" row label. */
  label: string;
  icon?: IconName;
  /** The related workflow template the hop lands on. */
  workflowTemplateId: string;
};

/**
 * The hops a nested filter is reached through, outermost first: `[Asset, Meter]` for a filter on a
 * field of the Meter related to the Asset related to this workflow's card. Empty/absent means the
 * filter targets this workflow directly.
 *
 * When a `FilterOption` carries a path, its `id`/`type`/`label`/`icon`/`component` all describe
 * the component on the **innermost** workflow, so everything that switches on those keeps working
 * unchanged; only the encoder/decoder and the row label need to know about the hops.
 */
export type RelatedEntityPath = RelatedEntityHop[];

/**
 * How many `RELATED_CARD` hops a filter may travel. Bounded by the `FilterByComponent` GraphQL
 * fragment, which spells each level out because fragments can't recurse - the schema type itself
 * nests without limit.
 */
export const RELATED_ENTITY_MAX_DEPTH = 2;

export type FilterOption = ControlOption & {
  component: InputComponent;
  relatedEntityPath?: RelatedEntityPath;
  /**
   * An alternative scalar representation of `component` to filter against, chosen alongside the
   * component itself (see `getFilterScalarKey`). Absent means "filter on the component's own
   * value", which is the case for all but a handful of component types. Never set for a component
   * type whose scalar key is applied unconditionally - there is nothing to choose there.
   */
  scalarKey?: API.ScalarKey;
};

/**
 * Nested filter/group options for cross-workflow filtering, keyed by the path of `RELATED_CARD`
 * component ids they hang off (see `getRelatedEntityPathKey`). Resolved lazily - an absent key
 * means "that path isn't resolved yet", not "no nested options".
 */
export type RelatedFilterOptions = Map<string, FilterOption[]>;

export const EMPTY_RELATED_FILTER_OPTIONS: RelatedFilterOptions = new Map();

export type SortOption = ControlOption & {
  direction?: API.SortDirection;
};

export interface ControlRow<T = ControlOption> {
  onUpdate: (item: T) => void;
  onDelete: (item: T) => void;
}

export type FilterTagOption = {
  uuid?: string;
  label: string;
  value: string | number;
  backgroundColor?: Color | 'white';
  quantity?: number;
  filter?: FilterOption;
  user?: User;
  archived?: boolean;
};

// A PersonOption represents a selectable user within multi-select components.
// For now it shares the same structure as FilterTagOption but is defined as a
// distinct alias so we can use stricter generics in react-select components.
export type PersonOption = FilterTagOption;

// A RelatedCardOption represents a selectable related card within multi-select
// components. It too maps to the same shape but is kept distinct for typing
// clarity.
export type RelatedCardOption = FilterTagOption;

export type FilterTextSelection = {
  type: 'TEXT';
  operator: 'empty' | 'contains';
  value?: string;
};

export const numberOperators = ['EQ', 'LT', 'LTE', 'GT', 'GTE', 'empty'] as const;
export type NumberOperator = (typeof numberOperators)[number];

export type FilterNumberSelection = {
  type: 'NUMBER';
  operator: NumberOperator;
  value: number;
};

export function isNumberOperator(value: string): value is NumberOperator {
  return numberOperators.includes(value as NumberOperator);
}

export const comparisonOperators: API.ComparisonOperator[] = [
  'EQ',
  'GT',
  'GTE',
  'IS_ALL_OF',
  'IS_NONE_OF',
  'IS_ONE_OF',
  'LT',
  'LTE',
  'NEQ',
];

export function isComparisonOperator(value: string): value is API.ComparisonOperator {
  return comparisonOperators.includes(value as API.ComparisonOperator);
}

export function controlOptionToNumberSelection(
  operator: ControlOption,
  value: number,
): FilterNumberSelection | null {
  if (!isNumberOperator(operator.id)) {
    return null;
  }
  return { type: 'NUMBER', operator: operator.id, value };
}

/**
 * A yes/no filter. Contributed by a scalar representation rather than by any component type of its
 * own - a TIME_TRACKER read as `TIME_TRACKER_IS_ACTIVE_BOOLEAN` is the only one today. There is no
 * operator to pick: the comparison is always equality (see `getFilterComponent`).
 */
export type FilterBooleanSelection = {
  type: 'BOOLEAN';
  value: boolean;
};

/**
 * A filter that defers to a field on the card being edited rather than naming a value: "the
 * related workflow's Parent Location matches this card's Parent Location". Encoded as
 * `containsCurrentWorkflowTemplateComponentValue`, which only the dynamic filter union carries,
 * so it is only ever offered where `ControlsState['availableMatchComponents']` is non-empty - the
 * related card field config, and nowhere else.
 *
 * `componentId` is null between picking the operator and picking the field. Such a row encodes to
 * nothing, the same as any other half-filled row.
 */
export type FilterComponentMatchSelection = {
  type: 'COMPONENT_MATCH';
  componentId: string | null;
};

export type FilterTagSelection = {
  type: 'TAG';
  filter: FilterOption;
  value: 'empty' | string;
};

export type FilterPersonSelection = {
  type: 'PERSON';
  filter: FilterOption;
  value: 'empty' | 'currentUser' | number;
};

export type FilterRelatedCardSelection = {
  type: 'RELATED_CARD';
  filter: FilterOption;
  value: 'empty' | string;
  label?: string;
};

export type FilterDateOperator =
  | 'empty'
  | 'today'
  | 'exactly'
  | 'between'
  | 'startsOn'
  | 'endsOn'
  | 'daysBeforeToday'
  | 'daysAfterToday';

export type FilterDateSelection = {
  type: 'DATE';
  operator: FilterDateOperator;
  comparisonOperator?: API.ComparisonOperator;
  duration?: number;
  after?: string | undefined;
  before?: string | undefined;
  // When true, `startsOn`/`endsOn` are expressed relative to today via
  // `comparisonOperator` + signed `duration` (days) rather than absolute
  // `after`/`before`. The absolute bound is derived from `duration` at
  // save/filter time so it recomputes daily.
  isRelative?: boolean;
};

export type FilterSelectionMember =
  | FilterTagSelection
  | FilterPersonSelection
  | FilterRelatedCardSelection;

export type FilterSelection =
  | FilterTextSelection
  | FilterNumberSelection
  | FilterBooleanSelection
  | FilterComponentMatchSelection
  | FilterTagSelection[]
  | FilterPersonSelection[]
  | FilterRelatedCardSelection[]
  | FilterDateSelection;

export function isFilterDateSelection(
  selection: FilterSelection | undefined,
): selection is FilterDateSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'DATE';
}

export function isFilterComponentMatchSelection(
  selection: FilterSelection | undefined,
): selection is FilterComponentMatchSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'COMPONENT_MATCH';
}

export function isFilterTextSelection(
  selection: FilterSelection | undefined,
): selection is FilterTextSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'TEXT';
}

export function isFilterNumberSelection(
  selection: FilterSelection | undefined,
): selection is FilterNumberSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'NUMBER';
}

export function isFilterBooleanSelection(
  selection: FilterSelection | undefined,
): selection is FilterBooleanSelection {
  return !isNil(selection) && !Array.isArray(selection) && selection.type === 'BOOLEAN';
}

export function isFilterTagSelectionMember(
  selection: FilterSelectionMember | undefined,
): selection is FilterTagSelection {
  return !isNil(selection) && selection.type === 'TAG';
}

export function isFilterTagSelectionList(
  selection: FilterSelection | undefined,
): selection is FilterTagSelection[] {
  return Array.isArray(selection) && selection.every(isFilterTagSelectionMember);
}

export function isFilterPersonSelectionMember(
  selection: FilterSelectionMember | undefined,
): selection is FilterPersonSelection {
  return !isNil(selection) && selection.type === 'PERSON';
}

export function isFilterPersonSelectionList(
  selection: FilterSelection | undefined,
): selection is FilterPersonSelection[] {
  return Array.isArray(selection) && selection.every(isFilterPersonSelectionMember);
}

export function isFilterRelatedCardSelectionMember(
  selection: FilterSelectionMember | undefined,
): selection is FilterRelatedCardSelection {
  return !isNil(selection) && selection.type === 'RELATED_CARD';
}

export function isFilterRelatedCardSelectionList(
  selection: FilterSelection | undefined,
): selection is FilterRelatedCardSelection[] {
  return Array.isArray(selection) && selection.every(isFilterRelatedCardSelectionMember);
}

export function isFilterSelectionMemberList(
  selection: FilterSelection | undefined,
): selection is FilterTagSelection[] | FilterPersonSelection[] | FilterRelatedCardSelection[] {
  return Array.isArray(selection);
}

export function tagSelectionToTagOption(selection: FilterTagSelection): FilterTagOption {
  const { component } = selection.filter;
  const tagComponent = component && isTagComponent(component) ? component : undefined;
  if (selection.value === 'empty') {
    return { value: 'empty', label: tagComponent?.label ? `No ${tagComponent.label}` : '' };
  }
  const tagOption = tagComponent?.options?.find(o => o.value === selection.value);
  return {
    value: selection.value,
    label: tagOption?.label ?? '',
    backgroundColor: (tagOption?.backgroundColor ?? undefined) as
      | FilterTagOption['backgroundColor']
      | undefined,
    archived: tagOption?.archived ?? undefined,
    filter: selection.filter,
  };
}

export function relatedCardSelectionToRelatedCardOption(
  selection: FilterRelatedCardSelection,
): RelatedCardOption {
  return {
    value: selection.value,
    label: selection.label ?? '',
    filter: selection.filter,
  };
}

export function tagComponentOptionToFilterTagOption(
  option: API.TagComponentOption,
): FilterTagOption {
  return {
    value: option.value,
    label: option.label,
    // API.Color is a string union matching the Color enum values, cast is required at this boundary
    backgroundColor: option.backgroundColor as Color | undefined,
    archived: option.archived ?? undefined,
  };
}

export function tagOptionToTagSelection(
  option: FilterTagOption,
  filter: FilterOption,
): FilterTagSelection {
  return { type: 'TAG', filter, value: String(option.value) };
}

export function personOptionToPersonSelection(
  option: FilterTagOption,
  filter: FilterOption,
): FilterPersonSelection {
  return {
    type: 'PERSON',
    filter,
    value: isNumber(option.value) ? option.value : (option.value as 'empty' | 'currentUser'),
  };
}

export function relatedCardOptionToRelatedCardSelection(
  option: FilterTagOption,
  filter: FilterOption,
): FilterRelatedCardSelection {
  return { type: 'RELATED_CARD', filter, value: String(option.value), label: option.label };
}

export type GroupFilterSelection =
  | FilterTagSelection[]
  | FilterPersonSelection[]
  | FilterRelatedCardSelection[];

export type FilterSelectionState = {
  filter?: FilterOption;
  group?: FilterOption;
  selection: FilterSelection;
  negate: boolean;
};

export type SortSelectionState = {
  sort: SortOption;
  direction: API.SortDirection;
};

export type FilterSelectionStateGroup = Map<string, FilterSelectionState>;

export type FilterOperation =
  | 'addFilter'
  | 'removeFilter'
  | 'removeAllFilters'
  | 'addSelections'
  | 'updateSelections'
  | 'removeAllSelections';

export type SortOperation = 'addSort' | 'prependSort' | 'updateSort' | 'removeSort';

export type GroupOperation =
  | 'addGroup'
  | 'addFilter'
  | 'updateFilter'
  | 'removeFilter'
  | 'removeGroup'
  | 'removeAllGroups'
  | 'updateSelections';

// Shared callback type for setOption/setFilterOption across filter, group, and sort contexts.
// TSelection defaults to FilterSelection but can be narrowed (e.g. GroupFilterSelection).
export type SetOptionFn<TSelection extends FilterSelection = FilterSelection> = (
  filter: FilterOption,
  operation: FilterOperation,
  selection?: TSelection,
  negate?: boolean,
) => void;

export interface ControlsState {
  filters: Map<string, FilterSelectionState>;
  availableFilters: FilterOption[];
  selectedFilters: FilterOption[];

  sorts: Map<string, SortSelectionState>;
  availableSorts: SortOption[];
  selectedSorts: SortOption[];

  groups: Map<string, FilterSelectionState>[];
  availableGroups: FilterOption[];
  selectedGroups: FilterOption[][];

  /**
   * The components a filter row can match against - the fields of the card being edited, in the
   * related card field config. Empty everywhere else, which is what keeps the `Matches` operator
   * out of every other filter menu.
   */
  availableMatchComponents?: InputComponent[];

  /** Filterable components on related workflows, keyed by related-card component path. */
  availableRelatedFilters: RelatedFilterOptions;
  /** The groupable subset of the above. */
  availableRelatedGroups: RelatedFilterOptions;

  search: string | null;

  isModified?: boolean;
  showGroupMenu?: boolean;
}

// The value the search field renders, which changes on every keystroke - kept apart from
// `ControlsState.search` (the debounced query) so typing doesn't re-render every view that
// reads controls state.
export interface SearchInputState {
  searchInput: string | null;
}

export interface ControlsLoadingState {
  // true while any of the collection's card queries is in flight. A collection
  // runs one per view plus one per group and per expanded tree row, so this is
  // an OR over all of them.
  isLoading: boolean;
}

export interface ControlsErrorState {
  // true when any of the collection's card queries was refused because a cross-workflow filter
  // resolved more ids than core-api will traverse (`SEARCH_RELATED_ENTITY_LIMIT_EXCEEDED`). The
  // refusal names no component, so this is a collection-wide flag rather than a per-filter one.
  hasRelatedEntityLimitError: boolean;
}

export interface ControlsActions {
  dispatchFilterAction: (
    filter: FilterOption | undefined,
    operation: FilterOperation,
    selection?: FilterSelection,
    negate?: boolean,
  ) => void;
  dispatchSortAction: (sort: SortOption, operation: SortOperation) => void;
  // Registers one card query's in-flight state under a caller-stable id.
  setLoading: (id: string, isLoading: boolean) => void;
  // Registers one card query's related-entity limit refusal under a caller-stable id.
  setRelatedEntityLimitError: (id: string, hasError: boolean) => void;
  dispatchGroupAction: (
    filter: FilterOption | undefined,
    operation: GroupOperation,
    index?: number,
    selection?: FilterSelection,
  ) => void;
  /**
   * Registers demand for the nested filter options at one or more `RELATED_CARD` component paths,
   * triggering a fetch of the related workflow templates each path travels through. Called when a
   * related card is expanded in a filter/group menu, and when applied criteria turns out to
   * contain a `matchesRelatedEntity` filter. Idempotent.
   */
  requestRelatedFilterOptions(paths: string[][]): void;
  setSearch: Dispatch<SetStateAction<string | null>>;
  revertCollectionCriteria(): void;
  updateViewTemplateCollectionCriteria(): void;
  setShowGroupMenu: Dispatch<SetStateAction<boolean>>;
}
